import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResumeTailoring } from '@/contexts/ResumeTailoringContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle2, Download, FileText, Mail, BarChart3, RefreshCw, Home, Linkedin, Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { StepHeader } from './StepHeader';

const DOC_META: Record<string, { label: string; icon: React.ElementType; filename: string }> = {
  'resume':         { label: 'Resume',          icon: FileText, filename: 'tailored-resume.txt' },
  'cover-letter':   { label: 'Cover Letter',    icon: Mail,     filename: 'cover-letter.txt' },
  'linkedin-profile': { label: 'LinkedIn',      icon: Linkedin, filename: 'linkedin-profile.txt' },
  'executive-bio':  { label: 'Executive Bio',   icon: FileText, filename: 'executive-bio.txt' },
  'thank-you-letter': { label: 'Thank You',     icon: FileText, filename: 'thank-you-letter.txt' },
  'outreach-email': { label: 'Outreach Email',  icon: Mail,     filename: 'outreach-email.txt' },
};

const CONTENT_MAP: Record<string, (s: ReturnType<typeof useResumeTailoring>['state']) => string | undefined> = {
  'resume':           s => s.tailoredResumeContent,
  'cover-letter':     s => s.coverLetterContent,
  'linkedin-profile': s => s.linkedinContent,
  'executive-bio':    s => s.executiveBioContent,
  'thank-you-letter': s => s.thankYouLetterContent,
  'outreach-email':   s => s.outreachEmailContent,
};

export function ResultsPage() {
  const navigate = useNavigate();
  const { state, dispatch } = useResumeTailoring();
  const { toast } = useToast();
  const [copiedDoc, setCopiedDoc] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  const availableDocs = state.selectedDocumentTypes
    .map(type => ({ type, content: CONTENT_MAP[type]?.(state), ...DOC_META[type] }))
    .filter(d => d.content);

  const handleDownload = async (type: string) => {
    const doc = availableDocs.find(d => d.type === type);
    if (!doc?.content) return;
    setDownloading(p => ({ ...p, [type]: true }));
    try {
      const blob = new Blob([doc.content!], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = doc.filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast({ title: `${doc.label} downloaded` });
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' });
    } finally {
      setDownloading(p => ({ ...p, [type]: false }));
    }
  };

  const handleCopy = async (type: string) => {
    const doc = availableDocs.find(d => d.type === type);
    if (!doc?.content) return;
    await navigator.clipboard.writeText(doc.content!);
    setCopiedDoc(type);
    toast({ title: `${doc.label} copied to clipboard` });
    setTimeout(() => setCopiedDoc(null), 2000);
  };

  const handleStartOver = () => {
    dispatch({ type: 'RESET_WIZARD' });
    navigate('/resume-tailoring-simple/step/1');
  };

  const matchScore = state.analysisData?.matchScore
    ? Math.round(state.analysisData.matchScore * 100)
    : null;

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8">
      <StepHeader currentStep={5} />

      {/* Success banner */}
      <Card className="mb-6 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-green-900">Documents Ready!</h2>
              <p className="text-sm text-green-700 mt-0.5">
                {availableDocs.length} document{availableDocs.length !== 1 ? 's' : ''} tailored for <strong>{state.jobPositionTitle || 'your target role'}</strong>
                {matchScore !== null && ` · ${matchScore}% match`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick download row */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            Download Documents
          </CardTitle>
          <CardDescription>Save your tailored documents to your device</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {availableDocs.map(doc => {
              const Icon = doc.icon;
              return (
                <div key={doc.type} className="flex items-center gap-2 p-3 rounded-xl border border-border bg-muted/30">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <span className="flex-1 font-medium text-sm">{doc.label}</span>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => handleCopy(doc.type)}
                    className="h-8 px-2"
                    title="Copy to clipboard"
                  >
                    {copiedDoc === doc.type ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDownload(doc.type)}
                    disabled={downloading[doc.type]}
                    className="h-8 px-3 text-xs"
                  >
                    {downloading[doc.type] ? '…' : 'Download'}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Preview tabs */}
      {availableDocs.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Preview</CardTitle>
            <CardDescription>Review your documents before downloading</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={availableDocs[0].type}>
              <TabsList className="flex flex-wrap gap-1 h-auto mb-4 bg-muted p-1 rounded-lg">
                {availableDocs.map(doc => {
                  const Icon = doc.icon;
                  return (
                    <TabsTrigger key={doc.type} value={doc.type} className="flex items-center gap-1.5 text-xs">
                      <Icon className="w-3 h-3" /> {doc.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
              {availableDocs.map(doc => (
                <TabsContent key={doc.type} value={doc.type}>
                  <div className="relative">
                    <div className="bg-muted/50 rounded-xl p-5 max-h-[450px] overflow-y-auto border border-border">
                      <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">{doc.content}</pre>
                    </div>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleCopy(doc.type)}
                      className="absolute top-3 right-3 h-7 px-2 text-xs gap-1"
                    >
                      {copiedDoc === doc.type ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Analysis */}
      {state.analysisData && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" /> Tailoring Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {matchScore !== null && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Match Score</span>
                <Badge className="bg-green-100 text-green-800 border-green-200">{matchScore}%</Badge>
              </div>
            )}
            {state.analysisData.keywordsAdded?.length > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Keywords Added</span>
                <Badge variant="secondary">{state.analysisData.keywordsAdded.length}</Badge>
              </div>
            )}
            {state.analysisData.sectionsOptimized?.length > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Sections Optimized</span>
                <Badge variant="secondary">{state.analysisData.sectionsOptimized.length}</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Button variant="outline" onClick={handleStartOver} className="flex-1">
          <RefreshCw className="mr-2 h-4 w-4" /> Tailor Another Resume
        </Button>
        <Button onClick={() => navigate('/')} className="flex-1">
          <Home className="mr-2 h-4 w-4" /> Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
